import React, { useState } from 'react';
import { Form, Input, Button, Checkbox, Typography, App, Alert } from 'antd';
import { UserOutlined, LockOutlined, SafetyOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import authService from '../services/authService';
import styles from './Login.module.css';

const { Title, Text } = Typography;

const Login = ({ onLoginSuccess }) => {
  const { message } = App.useApp();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [requires2FA, setRequires2FA] = useState(false);
  const [userEmail, setUserEmail] = useState('');
  const [useBackupCode, setUseBackupCode] = useState(false);
  const navigate = useNavigate();

  const onFinish = async (values) => {
    setLoading(true);
    try {
      if (requires2FA) {
        // Handle 2FA verification
        const verificationCode = values.verificationCode;
        const result = await authService.verify2FALogin(userEmail, verificationCode, useBackupCode);
        
        if (result.success) {
          message.success('Login successful! Welcome to TapLearnGo.');
          
          if (onLoginSuccess) {
            onLoginSuccess(result.data.user);
          }
        } else {
          message.error(result.message || 'Invalid verification code.');
        }
      } else {
        // Handle regular login
        const result = await authService.login(values.email, values.password);
        
        if (result.success) {
          if (result.requiresTwoFactor) {
            // User needs to provide 2FA code
            setRequires2FA(true);
            setUserEmail(values.email);
            form.resetFields(['password', 'verificationCode']);
            message.info('Please enter your authentication code');
          } else {
            // Regular login success
            message.success('Login successful! Welcome to TapLearnGo.');
            
            if (onLoginSuccess) {
              onLoginSuccess(result.data.user);
            }
          }
        } else {
          message.error(result.message || 'Please check your credentials and try again.');
        }
      }
    } catch (error) {
      console.error('Login error:', error);
      message.error('Unable to connect to server. Please check your internet connection and try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = () => {
    navigate('/forgot-password');
  };

  const handleBackToLogin = () => {
    setRequires2FA(false);
    setUserEmail('');
    setUseBackupCode(false);
    form.resetFields();
  };

  const toggleBackupCode = () => {
    setUseBackupCode(!useBackupCode);
    form.resetFields(['verificationCode']);
  };

  const onFinishFailed = (errorInfo) => {
    console.log('Failed:', errorInfo);
    message.error('Please check your input and try again.');
  };

  return (
    <div className={styles.loginContainer}>
      {/* Left side - TapLearnGo features section */}
      <div className={styles.leftSide}>
        <div className={styles.leftContent}>
          <div className={styles.badge}>
            Fast Fun Micro Learning
          </div>
          
          <Title level={1} className={styles.mainTitle}>
            Create a Learning Culture
          </Title>
          
          <Text className={styles.description}>
            Create a culture of learning in just a few minutes each week. Learners can quickly understand new information and pick up new skills through quick, fun and impactful micro-learning experiences.
          </Text>
          
          <div className={styles.features}>
            <div className={styles.featureItem}>
              <span className={styles.checkMark}>✓</span>
              <Text className={styles.featureText}>Completed in 2 minutes</Text>
            </div>
            <div className={styles.featureItem}>
              <span className={styles.checkMark}>✓</span>
              <Text className={styles.featureText}>Instant results & measurable impact</Text>
            </div>
            <div className={styles.featureItem}>
              <span className={styles.checkMark}>✓</span>
              <Text className={styles.featureText}>No passwords, no app downloads</Text>
            </div>
            <div className={styles.featureItem}>
              <span className={styles.checkMark}>✓</span>
              <Text className={styles.featureText}>Promotes friendly competition</Text>
            </div>
          </div>
          
          <div className={styles.whySection}>
            <Title level={3} className={styles.whyTitle}>
              Why TapLearnGo?
            </Title>
            <Text className={styles.whyText}>
              Making learning easy, effective and fun to do. Our micro-learning platform is designed for humans, with minimal disruption and maximum engagement.
            </Text>
          </div>
        </div>
      </div>

      {/* Right side - Login Form */}
      <div className={styles.rightSide}>
        {/* Mobile header - only visible on mobile */}
        <div className={styles.mobileHeader}>
          <Title level={2} className={styles.mobileTitle}>
            TapLearnGo
          </Title>
          <Text className={styles.mobileSubtitle}>
            Fast Fun Micro Learning
          </Text>
        </div>

        <div className={styles.formContainer}>
          {/* Logo */}
          <div style={{ marginBottom: '40px', textAlign: 'center' }}>
            <img 
              src="/logo.png" 
              alt="TapLearnGo Logo"
              style={{ 
                maxWidth: '350px',
                height: 'auto',
                marginBottom: '8px',
                display: 'block',
                margin: '0 auto'
              }}
              onError={(e) => {
                e.target.style.display = 'none';
              }}
            />
          </div>
          
          {/* Welcome Message */}
          <Title level={2} style={{ 
            color: '#2c3e50',
            marginBottom: '8px',
            fontSize: '28px',
            fontWeight: '400'
          }}>
            {requires2FA ? 'Two-Factor Authentication' : 'Welcome back!'}
          </Title>
          
          {requires2FA && (
            <Alert
              message={useBackupCode ? "Enter Backup Code" : "Two-Factor Authentication"}
              description={
                useBackupCode 
                  ? "Enter one of your backup recovery codes"
                  : "Enter the 6-digit code from your authenticator app"
              }
              type="info"
              showIcon
              icon={<SafetyOutlined />}
              style={{ 
                marginBottom: '24px',
                textAlign: 'left'
              }}
            />
          )}
          
          
          
          <Form
            form={form}
            name="login"
            onFinish={onFinish}
            onFinishFailed={onFinishFailed}
            autoComplete="off"
            layout="vertical"
            style={{ textAlign: 'left' }}
          >
            {!requires2FA ? (
              <>
                <Form.Item
                  label={<span style={{ color: '#2c3e50', fontWeight: '500' }}>Username</span>}
                  name="email"
                  rules={[
                    { required: true, message: 'Email is required' },
                    { type: 'email', message: 'Please enter a valid email' }
                  ]}
                  style={{ marginBottom: '20px' }}
                >
                  <Input
                    prefix={<UserOutlined style={{ color: '#bdc3c7' }} />}
                    placeholder="Enter your email"
                    size="large"
                    style={{
                      borderRadius: '6px',
                      border: '1px solid #ddd',
                      padding: '12px'
                    }}
                  />
                </Form.Item>

                <Form.Item
                  label={<span style={{ color: '#2c3e50', fontWeight: '500' }}>Password</span>}
                  name="password"
                  rules={[{ required: true, message: 'Password is required' }]}
                  style={{ marginBottom: '32px' }}
                >
                  <Input.Password
                    prefix={<LockOutlined style={{ color: '#bdc3c7' }} />}
                    placeholder="Enter your password"
                    size="large"
                    style={{
                      borderRadius: '6px',
                      border: '1px solid #ddd',
                      padding: '12px'
                    }}
                  />
                </Form.Item>
              </>
            ) : (
              <Form.Item
                label={<span style={{ color: '#2c3e50', fontWeight: '500' }}>
                  {useBackupCode ? 'Backup Recovery Code' : 'Authentication Code'}
                </span>}
                name="verificationCode"
                rules={[
                  { required: true, message: useBackupCode ? 'Backup code is required' : 'Authentication code is required' },
                  useBackupCode 
                    ? { len: 8, message: 'Backup code must be 8 characters' }
                    : { len: 6, message: 'Code must be 6 digits' }
                ]}
                style={{ marginBottom: '32px' }}
              >
                <Input
                  prefix={<SafetyOutlined style={{ color: '#bdc3c7' }} />}
                  placeholder={useBackupCode ? "Enter 8-character code" : "Enter 6-digit code"}
                  size="large"
                  maxLength={useBackupCode ? 8 : 6}
                  style={{
                    borderRadius: '6px',
                    border: '1px solid #ddd',
                    padding: '12px',
                    fontSize: '18px',
                    letterSpacing: useBackupCode ? '2px' : '6px',
                    textAlign: 'center',
                    fontFamily: 'monospace'
                  }}
                />
              </Form.Item>
            )}

            <Form.Item>
              <Button
                type="primary"
                htmlType="submit"
                loading={loading}
                size="large"
                style={{
                  width: '100%',
                  backgroundColor: '#2E86AB',
                  borderColor: '#2E86AB',
                  fontWeight: 'bold',
                  height: '48px',
                  borderRadius: '6px',
                  fontSize: '16px',
                  marginBottom: '16px'
                }}
              >
                {requires2FA ? 'Verify & Continue' : 'Log In'}
              </Button>
            </Form.Item>
            
            <div style={{ textAlign: 'center', marginBottom: '24px' }}>
              {requires2FA ? (
                <>
                  <button 
                    type="button"
                    onClick={toggleBackupCode}
                    style={{
                      color: '#2E86AB',
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      fontSize: '14px',
                      textDecoration: 'underline',
                      marginBottom: '8px',
                      display: 'block',
                      width: '100%'
                    }}
                  >
                    {useBackupCode ? 'Use authenticator code instead' : 'Use backup code instead'}
                  </button>
                  <button 
                    type="button"
                    onClick={handleBackToLogin}
                    style={{
                      color: '#95a5a6',
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      fontSize: '14px',
                      textDecoration: 'underline'
                    }}
                  >
                    Back to login
                  </button>
                </>
              ) : (
                <button 
                  type="button"
                  onClick={handleForgotPassword}
                  style={{
                    color: '#2E86AB',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    fontSize: '14px',
                    textDecoration: 'underline'
                  }}
                >
                  Forgot password?
                </button>
              )}
            </div>
            
            <div style={{ 
              textAlign: 'center',
              color: '#bdc3c7',
              fontSize: '12px',
              marginTop: '16px'
            }}>
              © 2025 TapLearnGo. All rights reserved.
            </div>
          </Form>
        </div>
      </div>
    </div>
  );
};

export default Login;